/**
 * Payment Schedule tab on the project detail page.
 *
 * Renders two cards — contractual documents (contract / PO / other) and
 * scheduled payment milestones — and wires all interactions to the auth-gated
 * AJAX endpoints under /projects/project/<pk>/payment-schedule/. No nested
 * <form>: this tab lives inside #project-form, so everything goes through
 * fetch + FormData.
 */
import * as bootstrap from 'bootstrap';

function getCsrfToken() {
    const el = document.querySelector('[name=csrfmiddlewaretoken]');
    return el ? el.value : '';
}

function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str == null ? '' : str;
    return div.innerHTML;
}

function triggerDownload(url) {
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
}

const STATUS_BADGE = {
    scheduled: { label: 'Scheduled', cls: 'bg-secondary' },
    requested: { label: 'Invoice requested', cls: 'bg-info text-dark' },
    created: { label: 'Invoice created', cls: 'bg-warning text-dark' },
    submitted: { label: 'Invoice submitted', cls: 'bg-primary' },
    overdue: { label: 'Invoice overdue', cls: 'bg-danger' },
    paid: { label: 'Paid', cls: 'bg-success' },
};

export function initPaymentSchedule() {
    const root = document.getElementById('payment-schedule-app');
    if (!root) return;

    const urls = {
        data: root.dataset.urlData,
        documentAdd: root.dataset.urlDocumentAdd,
        documentDelete: root.dataset.urlDocumentDelete,
        documentDownload: root.dataset.urlDocumentDownload,
        milestoneAdd: root.dataset.urlMilestoneAdd,
        milestoneUpdate: root.dataset.urlMilestoneUpdate,
        milestoneDelete: root.dataset.urlMilestoneDelete,
        milestoneReorder: root.dataset.urlMilestoneReorder,
        requestInvoice: root.dataset.urlRequestInvoice,
        invoiceUpload: root.dataset.urlInvoiceUpload,
        invoiceDownload: root.dataset.urlInvoiceDownload,
    };

    let projectCurrency = 'EUR';
    let projectBudget = null;
    let projectBudgetDisplay = '';
    let budgetUndisclosed = false;

    // Move the modal to <body> to escape the .main-content stacking context.
    const modalEl = document.getElementById('ps-milestone-modal');
    if (modalEl) document.body.appendChild(modalEl);

    const docsBody = root.querySelector('#ps-documents-table tbody');
    const docsEmpty = root.querySelector('#ps-documents-empty');
    const milestonesBody = root.querySelector('#ps-milestones-table tbody');
    const milestonesEmpty = root.querySelector('#ps-milestones-empty');
    // The modal and hidden invoice input live as siblings outside #payment-schedule-app.
    const invoiceFileInput = document.getElementById('ps-invoice-file-input');

    async function postForm(url, formData) {
        const resp = await fetch(url, {
            method: 'POST',
            headers: { 'X-CSRFToken': getCsrfToken() },
            body: formData,
        });
        if (!resp.ok) {
            const text = await resp.text();
            throw new Error(text || `HTTP ${resp.status}`);
        }
        return resp.json();
    }

    // --- Rendering ---------------------------------------------------------
    function renderDocuments(documents) {
        docsBody.innerHTML = '';
        docsEmpty.style.display = documents.length ? 'none' : '';
        documents.forEach(d => {
            const tr = document.createElement('tr');
            tr.innerHTML = `
                <td><span class="badge bg-light text-dark">${escapeHtml(d.document_type_label)}</span></td>
                <td>${escapeHtml(d.filename)}</td>
                <td>${escapeHtml(d.comment)}</td>
                <td class="text-muted small">${escapeHtml(d.uploaded_at)}</td>
                <td class="text-end text-nowrap">
                    <button type="button" class="btn btn-sm btn-outline-secondary" data-action="doc-download" data-id="${d.id}" title="Download"><i class="fas fa-download"></i></button>
                    <button type="button" class="btn btn-sm btn-outline-danger" data-action="doc-delete" data-id="${d.id}" title="Delete"><i class="fas fa-trash"></i></button>
                </td>`;
            docsBody.appendChild(tr);
        });
    }

    /** Sum the milestones per currency: total, invoiced and paid. */
    function milestoneTotals(milestones) {
        const byCurrency = {};
        for (const m of milestones) {
            const amount = parseFloat(m.amount);
            if (!Number.isFinite(amount)) continue;
            const code = m.amount_currency || projectCurrency;
            const bucket = byCurrency[code]
                || (byCurrency[code] = { total: 0, invoiced: 0, paid: 0 });
            bucket.total += amount;
            // "Invoiced" is every milestone an invoice exists for, paid ones
            // included — they were invoiced before they were paid.
            if (m.invoice_issued) bucket.invoiced += amount;
            if (m.payment_date) bucket.paid += amount;
        }
        return byCurrency;
    }

    function money(value, currency) {
        return `${value.toLocaleString('en-GB', {
            minimumFractionDigits: 2, maximumFractionDigits: 2,
        })} ${currency}`;
    }

    /**
     * Totals row per currency, plus a warning when the schedule does not add up
     * to the contract — the mistake this table exists to catch.
     */
    function renderMilestoneTotals(milestones) {
        const foot = root.querySelector('#ps-milestones-foot');
        const warning = root.querySelector('#ps-budget-warning');
        const warningText = root.querySelector('#ps-budget-warning-text');
        foot.innerHTML = '';
        warning.style.display = 'none';
        if (!milestones.length) return;

        const totals = milestoneTotals(milestones);
        const codes = Object.keys(totals).sort();
        for (const code of codes) {
            const t = totals[code];
            const tr = document.createElement('tr');
            tr.innerHTML = `
                <td></td>
                <td class="ps-total-label">Total${codes.length > 1 ? ` ${escapeHtml(code)}` : ''}</td>
                <td></td>
                <td class="text-end text-nowrap">${escapeHtml(money(t.total, code))}</td>
                <td colspan="6" class="ps-total-sub">
                    Invoiced ${escapeHtml(money(t.invoiced, code))}
                    &middot; Paid ${escapeHtml(money(t.paid, code))}
                    &middot; Outstanding ${escapeHtml(money(t.total - t.paid, code))}
                </td>
                <td></td>
                <td></td>`;
            foot.appendChild(tr);
        }

        // Compare only like with like: the budget's own currency.
        const budget = parseFloat(projectBudget);
        if (!Number.isFinite(budget) || budgetUndisclosed) return;
        const matching = totals[projectCurrency];
        const otherCurrencies = codes.filter((c) => c !== projectCurrency);

        // Nothing in the budget's currency: the mismatch *is* the message, and
        // "totals 0.00" would bury it.
        if (!matching) {
            warningText.textContent =
                `The milestones are in ${otherCurrencies.join(', ')} while the project budget `
                + `is ${projectBudgetDisplay}, so they cannot be compared. Either the budget `
                + 'or the milestone currencies need correcting.';
            warning.style.display = '';
            return;
        }

        const diff = matching.total - budget;
        if (Math.abs(diff) < 0.01 && !otherCurrencies.length) return;

        const parts = [];
        if (Math.abs(diff) >= 0.01) {
            parts.push(`The milestones total ${money(matching.total, projectCurrency)}, `
                + `which is ${money(Math.abs(diff), projectCurrency)} `
                + `${diff > 0 ? 'more than' : 'short of'} the project budget of `
                + `${projectBudgetDisplay}.`);
        }
        if (otherCurrencies.length) {
            parts.push(`${otherCurrencies.join(', ')} milestone(s) are not counted in that `
                + 'comparison — they are in a different currency from the budget.');
        }
        warningText.textContent = parts.join(' ');
        warning.style.display = '';
    }

    function renderMilestones(milestones) {
        milestonesBody.innerHTML = '';
        milestonesEmpty.style.display = milestones.length ? 'none' : '';
        milestones.forEach((m, idx) => {
            const badge = STATUS_BADGE[m.status] || STATUS_BADGE.scheduled;
            const tr = document.createElement('tr');
            const invoiceCell = m.has_invoice_file
                ? `${escapeHtml(m.invoice_number)} <button type="button" class="btn btn-sm btn-link p-0 ms-1" data-action="invoice-download" data-id="${m.id}" title="Download invoice"><i class="fas fa-file-download"></i></button>`
                : escapeHtml(m.invoice_number);
            const overdue = m.status === 'overdue';
            tr.innerHTML = `
                <td class="ps-drag-handle text-muted" title="Drag to reorder" draggable="true"><i class="fas fa-grip-vertical"></i></td>
                <td>${escapeHtml(m.name)}${m.comment ? `<i class="fas fa-comment-dots text-muted ms-1" title="${escapeHtml(m.comment)}"></i>` : ''}</td>
                <td class="text-nowrap">${escapeHtml(m.due_date)}</td>
                <td class="text-end text-nowrap">${escapeHtml(m.amount_display)}</td>
                <td><span class="badge ${badge.cls}">${escapeHtml(badge.label)}</span></td>
                <td class="text-nowrap">${escapeHtml(m.invoice_sent_date)}</td>
                <td class="text-nowrap">${escapeHtml(m.invoice_date)}</td>
                <td class="text-center">${m.invoice_date ? escapeHtml(String(m.payment_term_days)) : ''}</td>
                <td class="text-nowrap${overdue ? ' text-danger fw-bold' : ''}">${escapeHtml(m.payment_due_date)}</td>
                <td>${invoiceCell}</td>
                <td class="text-nowrap">${escapeHtml(m.payment_date)}</td>
                <td class="text-end text-nowrap">
                    <button type="button" class="btn btn-sm btn-outline-primary" data-action="request-invoice" data-id="${m.id}" title="Request invoice from finance"><i class="fas fa-paper-plane"></i></button>
                    <button type="button" class="btn btn-sm btn-outline-secondary" data-action="invoice-upload" data-id="${m.id}" title="Upload invoice"><i class="fas fa-upload"></i></button>
                    <button type="button" class="btn btn-sm btn-outline-secondary" data-action="milestone-edit" data-id="${m.id}" title="Edit"><i class="fas fa-pen"></i></button>
                    <button type="button" class="btn btn-sm btn-outline-danger" data-action="milestone-delete" data-id="${m.id}" title="Delete"><i class="fas fa-trash"></i></button>
                </td>`;
            tr.dataset.milestone = JSON.stringify(m);
            milestonesBody.appendChild(tr);
        });
    }

    // --- Drag-to-reorder ---------------------------------------------------
    let dragRow = null;

    function currentOrderIds() {
        return Array.from(milestonesBody.querySelectorAll('tr'))
            .map(tr => JSON.parse(tr.dataset.milestone).id);
    }

    async function persistOrder() {
        const fd = new FormData();
        fd.append('order', currentOrderIds().join(','));
        try { await postForm(urls.milestoneReorder, fd); await reload(); }
        catch (err) { alert('Reorder failed: ' + err.message); await reload(); }
    }

    milestonesBody.addEventListener('dragstart', (e) => {
        const handle = e.target.closest('.ps-drag-handle');
        if (!handle) { e.preventDefault(); return; }
        dragRow = handle.closest('tr');
        dragRow.classList.add('ps-dragging');
        e.dataTransfer.effectAllowed = 'move';
    });

    milestonesBody.addEventListener('dragover', (e) => {
        if (!dragRow) return;
        e.preventDefault();
        const over = e.target.closest('tr');
        if (!over || over === dragRow) return;
        const rect = over.getBoundingClientRect();
        const after = (e.clientY - rect.top) > rect.height / 2;
        milestonesBody.insertBefore(dragRow, after ? over.nextSibling : over);
    });

    milestonesBody.addEventListener('drop', (e) => e.preventDefault());

    milestonesBody.addEventListener('dragend', async () => {
        if (!dragRow) return;
        dragRow.classList.remove('ps-dragging');
        dragRow = null;
        await persistOrder();
    });

    async function reload() {
        try {
            const resp = await fetch(urls.data);
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const data = await resp.json();
            projectCurrency = data.project_currency || 'EUR';
            projectBudget = data.project_budget;
            projectBudgetDisplay = data.project_budget_display || '';
            budgetUndisclosed = !!data.undisclosed_budget;
            renderDocuments(data.documents || []);
            renderMilestones(data.milestones || []);
            renderMilestoneTotals(data.milestones || []);
        } catch (err) {
            console.error('Failed to load payment schedule:', err);
        }
    }

    // --- Contractual documents --------------------------------------------
    const uploadBtn = root.querySelector('#ps-doc-upload-btn');
    uploadBtn.addEventListener('click', async () => {
        const fileInput = root.querySelector('#ps-doc-file');
        const file = fileInput.files[0];
        if (!file) { alert('Please choose a file to upload.'); return; }
        const fd = new FormData();
        fd.append('document_type', root.querySelector('#ps-doc-type').value);
        fd.append('comment', root.querySelector('#ps-doc-comment').value);
        fd.append('file', file);
        uploadBtn.disabled = true;
        try {
            await postForm(urls.documentAdd, fd);
            fileInput.value = '';
            root.querySelector('#ps-doc-comment').value = '';
            await reload();
        } catch (err) {
            alert('Upload failed: ' + err.message);
        } finally {
            uploadBtn.disabled = false;
        }
    });

    docsBody.addEventListener('click', async (e) => {
        const btn = e.target.closest('button[data-action]');
        if (!btn) return;
        const id = btn.dataset.id;
        if (btn.dataset.action === 'doc-download') {
            triggerDownload(`${urls.documentDownload}?doc_id=${id}`);
        } else if (btn.dataset.action === 'doc-delete') {
            if (!confirm('Delete this document?')) return;
            const fd = new FormData();
            fd.append('doc_id', id);
            try { await postForm(urls.documentDelete, fd); await reload(); }
            catch (err) { alert('Delete failed: ' + err.message); }
        }
    });

    // --- Milestones --------------------------------------------------------
    const modal = modalEl ? bootstrap.Modal.getOrCreateInstance(modalEl) : null;
    const f = (id) => modalEl.querySelector(id);

    function updatePaymentDue() {
        const inv = f('#ps-m-invoice-date').value;
        const term = parseInt(f('#ps-m-payment-term').value, 10);
        if (inv && !Number.isNaN(term)) {
            const d = new Date(inv + 'T00:00:00');
            d.setDate(d.getDate() + term);
            f('#ps-m-payment-due').value = d.toISOString().slice(0, 10);
        } else {
            f('#ps-m-payment-due').value = '';
        }
    }

    function openMilestoneModal(m) {
        f('#ps-m-id').value = m ? m.id : '';
        f('#ps-m-name').value = m ? m.name : '';
        f('#ps-m-due').value = m ? m.due_date : '';
        f('#ps-m-amount').value = m ? m.amount : '';
        f('#ps-m-currency').value = m ? (m.amount_currency || projectCurrency) : projectCurrency;
        f('#ps-m-invoice-sent-date').value = m ? m.invoice_sent_date : '';
        f('#ps-m-invoice-date').value = m ? m.invoice_date : '';
        f('#ps-m-payment-term').value = m ? m.payment_term_days : 30;
        f('#ps-m-invoice-number').value = m ? m.invoice_number : '';
        f('#ps-m-payment-date').value = m ? m.payment_date : '';
        f('#ps-m-comment').value = m ? m.comment : '';
        updatePaymentDue();
        modalEl.querySelector('#ps-milestone-modal-title').textContent = m ? 'Edit milestone' : 'Add milestone';
        modal.show();
        setTimeout(() => f('#ps-m-name').focus(), 150);
    }

    f('#ps-m-invoice-date').addEventListener('input', updatePaymentDue);
    f('#ps-m-payment-term').addEventListener('input', updatePaymentDue);

    root.querySelector('#ps-add-milestone-btn').addEventListener('click', () => openMilestoneModal(null));

    f('#ps-m-save-btn').addEventListener('click', async () => {
        const id = f('#ps-m-id').value;
        const fd = new FormData();
        if (id) fd.append('milestone_id', id);
        fd.append('name', f('#ps-m-name').value);
        fd.append('due_date', f('#ps-m-due').value);
        fd.append('amount', f('#ps-m-amount').value);
        fd.append('amount_currency', f('#ps-m-currency').value);
        fd.append('invoice_sent_date', f('#ps-m-invoice-sent-date').value);
        fd.append('invoice_date', f('#ps-m-invoice-date').value);
        fd.append('payment_term_days', f('#ps-m-payment-term').value);
        fd.append('invoice_number', f('#ps-m-invoice-number').value);
        fd.append('payment_date', f('#ps-m-payment-date').value);
        fd.append('comment', f('#ps-m-comment').value);
        const saveBtn = f('#ps-m-save-btn');
        saveBtn.disabled = true;
        try {
            await postForm(id ? urls.milestoneUpdate : urls.milestoneAdd, fd);
            modal.hide();
            await reload();
        } catch (err) {
            alert('Save failed: ' + err.message);
        } finally {
            saveBtn.disabled = false;
        }
    });

    let uploadTargetId = null;
    invoiceFileInput.addEventListener('change', async () => {
        const file = invoiceFileInput.files[0];
        if (!file || !uploadTargetId) return;
        const fd = new FormData();
        fd.append('milestone_id', uploadTargetId);
        fd.append('file', file);
        try {
            const res = await postForm(urls.invoiceUpload, fd);
            await reload();
            if (res && res.no_recipient) {
                alert('The invoice was uploaded, but nobody could be notified: this milestone has no invoice requester and the project has no staffed project leader.');
            }
        }
        catch (err) { alert('Invoice upload failed: ' + err.message); }
        finally { invoiceFileInput.value = ''; uploadTargetId = null; }
    });

    milestonesBody.addEventListener('click', async (e) => {
        const btn = e.target.closest('button[data-action]');
        if (!btn) return;
        const id = btn.dataset.id;
        const row = btn.closest('tr');
        const m = row && row.dataset.milestone ? JSON.parse(row.dataset.milestone) : null;
        const action = btn.dataset.action;

        if (action === 'milestone-edit') {
            openMilestoneModal(m);
        } else if (action === 'milestone-delete') {
            if (!confirm('Delete this milestone?')) return;
            const fd = new FormData();
            fd.append('milestone_id', id);
            try { await postForm(urls.milestoneDelete, fd); await reload(); }
            catch (err) { alert('Delete failed: ' + err.message); }
        } else if (action === 'request-invoice') {
            if (!confirm('Send an invoice request to the finance team for this milestone?')) return;
            const fd = new FormData();
            fd.append('milestone_id', id);
            btn.disabled = true;
            try {
                const res = await postForm(urls.requestInvoice, fd);
                await reload();
                if (res && res.email_sent === false) {
                    alert('The milestone was marked as requested, but the email could not be sent. Please notify finance manually.');
                }
            } catch (err) {
                alert('Request failed: ' + err.message);
            } finally {
                btn.disabled = false;
            }
        } else if (action === 'invoice-upload') {
            uploadTargetId = id;
            invoiceFileInput.click();
        } else if (action === 'invoice-download') {
            triggerDownload(`${urls.invoiceDownload}?milestone_id=${id}`);
        }
    });

    reload();
}
